import React, { useState, useEffect } from "react";
import { useSpring, animated } from 'react-spring';
import InteractiveAvatar from "../../components/avatar/InteractiveAvatar";
import bannerBg from "../../images/bg-blue-dots.svg";
import audioCloudPreview from "../../videos/audiocloud2.mov";
import senpaiPreview from "../../videos/senpaitv.mov";
import valorPreview from "../../videos/valor.mov";
import ProjectCard from "../project/ProjectCard";
import Brands from "../brands/Brands";
import Skills from "../skills/Skills";
import Experience from "../experience/Experience";
import Footer from "../footer/Footer";

function Landing() {
    const [show, setShow] = useState(false);
    const [showBanner, setShowBanner] = useState(false);
    const [showSubheader, setShowSubheader] = useState(false);
    const [showHamburger, setShowHamburger] = useState(false);
    const [showHamburgerMenu, setShowHamburgerMenu] = useState(false);

    useEffect(() => {
        const timer = setTimeout(() => {
            setShowSubheader(true);
        }, 500);
        return () => clearTimeout(timer);
    }, []);

    useEffect(() => {
        const timer = setTimeout(() => {
            setShow(true);
        }, 1000);
        return () => clearTimeout(timer);
    }, []);

    useEffect(() => {
        const timer = setTimeout(() => {
            setShowBanner(true);
        }, 1500);
        return () => clearTimeout(timer);
    }, []);

    useEffect(() => {
        const timer = setTimeout(() => {
            setShowHamburgerMenu(true);
        }, 1500);
        return () => clearTimeout(timer);
    }, []);

    const headerSpring = useSpring({
        from: {
            opacity: 0,
        },
        to: {
            opacity: showSubheader ? 1 : 0,
        },
        config: {
            tension: 100,
            friction: 10,
            mass: 1
        }
    });

    const bannerSpring = useSpring({
        from: {
            scaleX: 0,
            opacity: 0,
        },
        to: {
            scaleX: showBanner ? 1 : 0,
            opacity: showBanner ? 1 : 0,
        },
        config: {
            tension: 280,
            friction: 40,
        }
    });

    const avatarSpring = useSpring({
        from: {
            opacity: 0,
            transform: 'scale(0.6)',
        },
        to: {
            opacity: show ? 1 : 0,
            transform: show ? 'scale(1)' : 'scale(0.6)',
        },
        config: {
            tension: 400,
            friction: 10,
            mass: 1
        }
    });

    const hamburgerSpring = useSpring({
        from: {
            transform: 'translateX(-100%)',
        },
        to: {
            transform: showHamburgerMenu ? 'translateX(0)' : 'translateX(-100%)',
        },
        config: {
            tension: 180,
            friction: 20,
        }
    });
    const projectCards = [
        {
            title: "AudioCloud",
            videoSrc: audioCloudPreview,
            websiteUrl: "https://audiocloudapp-7c30720446d4.herokuapp.com/",
            description: "An audio platform and music sharing site consisting of listeners and creators. Find new music via navigating to your favorite artist's page or through curated playlists showcasing hot and new tracks. Got the talent for music creation? Upload your best tracks and showcase them to the online community!",
            techStack: ["Ruby", "JavaScript", "Ruby on Rails", "React/Redux", "PostgreSQL", "HTML Canvas", "Amazon S3", "AudioContext Web API"],
            bulletPoints: [
                "Combined vanilla JavaScript and Canvas API to create a custom interactive and responsive audio waveform visualizer that is unique to each song. The visualization is created by drawing graphics, the graphics’ properties being determined by extracted audio data.",
                "Created fully functional audio and image uploading and storage via Amazon S3. Uploading and rendering speed was optimized by using Rails ActiveStorage to directly link to S3.",
            ]
        },
        {
            title: "Valor",
            videoSrc: valorPreview,
            websiteUrl: "https://drumsinthedeep.github.io/Valor/",
            description: "A top-down roguelike video game in an old school arcade style. Fight your way through enemies and accumulate score before you meet your demise.",
            techStack: ["JavaScript", "Canvas API"],
            bulletPoints: [
                "Created fluid character and environment animation via the use of Canvas API, JavaScript’s requestAnimationFrame, sprite sheets, and GIMP.",
                "Implemented dynamic enemy npc movement using a modified A* algorithm for pathfinding.",
            ]
        },
        {
            title: "SenpaiTV",
            videoSrc: senpaiPreview,
            websiteUrl: "https://senpaiTV.app/",
            description: "A full-stack anime tracker and viewing companion. Search series, play episodes with a custom player, manage a personal watchlist, and sync progress, status, and scores with AniList—all in one place.",
            techStack: ["TypeScript", "Next.js", "React", "Tailwind CSS", "Prisma", "PostgreSQL", "NextAuth", "TanStack Query", "GraphQL", "Vercel"],
            bulletPoints: [
              "Integrated AniList OAuth + GraphQL to read/update list data (progress, status, score).",
              "Built a custom HTML5 video player with seek, mute, and next‑episode controls; auto-syncs progress at 80%.",
              "Implemented server actions and Prisma models for a persistent watchlist with status management.",
              "Added fast search and tile browsing with client caching via TanStack Query."
            ]
        }
    ];

    const handleHamburgerClick = () => {
        setShowHamburger(!showHamburger);
    };

    return (
        <div className="landingPageContainer">
            <animated.div style={hamburgerSpring} className="hamburger-container position-absolute">
                <div className="hamburger position-relative" onClick={handleHamburgerClick}></div>
                {showHamburger && (
                    <ul className="hamburger-menu d-flex flex-column position-absolute p-0">
                        <li className="hamburger-menu-item" onClick={() => window.open('mailto:daniel.ray.lancaster@gmail.com`', '_blank')}>Email</li>
                        <li className="hamburger-menu-item" onClick={() => window.open('https://www.linkedin.com/in/daniel-r-lancaster/', '_blank')}>LinkedIn</li>
                        <li className="hamburger-menu-item" onClick={() => window.open('https://github.com/drumsinthedeep', '_blank')}>GitHub</li>
                        <li className="hamburger-menu-item" onClick={() => window.open(process.env.PUBLIC_URL + '/daniel_lancaster_resume.pdf', '_blank')}>Resume</li>
                    </ul>
                )}
            </animated.div>
            <div className="d-flex flex-column flex-md-row avatar-container">
                <div className="d-flex header-container order-2 order-md-1">
                    <h1>Hi, Im Daniel</h1>
                    <animated.p style={headerSpring} className="header-text">I'm a full-stack software engineer in San Francisco, California. I build fast, polished, user-centric web experiences.</animated.p>
                </div>
                <div className="d-flex order-1 order-md-2">
                    <animated.img style={bannerSpring} className="banner-bg" src={bannerBg} />
                    <animated.div style={avatarSpring} className="interactive-container">
                        <InteractiveAvatar />
                    </animated.div>
                </div>
            </div>
            <div className="projects-container">
                {projectCards.map((card) => {
                    return <ProjectCard 
                        title={card.title} 
                        videoSrc={card.videoSrc} 
                        websiteUrl={card.websiteUrl}
                        description={card.description}
                        techStack={card.techStack}
                        bulletPoints={card.bulletPoints}
                    />
                })}
            </div>
            <div className="experience-header">
                <div className="experience-header-text d-flex">
                    <div className="d-flex">
                        <h1>4</h1><h1 className="plus-sign">+</h1>
                    </div>
                    <h3>years of industry experience</h3>
                </div>
                <div className="brand-text d-flex">
                    <div className="d-flex">
                        <h1>20</h1><h1 className="plus-sign">+</h1>
                    </div>
                    <h3>companies and brands</h3>
                </div>
            </div>
            <Brands />
            <Experience />
            <Skills />
            <Footer />
        </div>
    );
}

export default Landing;
